import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Avatar,
  Badge,
  Divider,
} from '@fluentui/react-components';
import {
  Grid24Regular,
  Apps24Regular,
  Money24Regular,
  PlugConnected24Regular,
  History24Regular,
  Settings24Regular,
  TaskListSquarePerson24Regular,
  PersonKey24Regular,
  ChevronRight24Regular,
  SignOut24Regular,
} from '@fluentui/react-icons';
import { useAppContext } from '@/store/appContext';
import { useAuth } from '@/hooks/useAuth';
import type { UserRole } from '@/models/types';
import { getInitials } from '@/utils/formatters';

interface SectionItem {
  path: string;
  label: string;
  description?: string;
  icon: JSX.Element;
  minRole?: UserRole;
  action?: () => void;
}

const ROLE_LEVEL: Record<UserRole, number> = { Member: 0, RO: 1, Admin: 2 };
const canAccess = (minRole: UserRole | undefined, role: UserRole) =>
  !minRole || ROLE_LEVEL[role] >= ROLE_LEVEL[minRole];

const ROLE_COLORS: Record<string, 'brand' | 'success' | 'informative'> = {
  Admin: 'brand',
  RO: 'success',
  Member: 'informative',
};

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '24px' },
  profileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
  },
  profileInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '3px',
  },
  profileName: {
    fontSize: '17px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  profileEmail: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  profileBadge: {
    alignSelf: 'flex-start' as const,
    marginTop: '4px',
  },
  section: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: '16px 20px 8px',
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 20px',
    cursor: 'pointer',
    minHeight: '54px',
    borderBottom: `0.5px solid ${tokens.colorNeutralStroke3}`,
    '&:last-child': { borderBottom: 'none' },
    '&:active': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  rowIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  rowLabel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  rowTitle: {
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    fontSize: '15px',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  rowDesc: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  rowChevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  dangerRow: {
    color: tokens.colorPaletteRedForeground1,
  },
  dangerIcon: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
});

export function More() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const { logout } = useAuth();

  if (!currentUser) return null;

  const sections: { title: string; items: SectionItem[] }[] = [
    {
      title: 'Organisation',
      items: [
        {
          path: '/applications',
          label: 'Applications',
          description: 'Catalog, licenses, costs',
          icon: <Apps24Regular />,
        },
        {
          path: '/allocations',
          label: 'Allocation Matrix',
          description: 'Project × People grid',
          icon: <TaskListSquarePerson24Regular />,
          minRole: 'RO',
        },
        {
          path: '/access',
          label: 'Access Matrix',
          description: 'Who has access to what',
          icon: <Grid24Regular />,
          minRole: 'RO',
        },
        {
          path: '/user-access',
          label: 'User Access',
          description: 'Per-user permissions',
          icon: <PersonKey24Regular />,
          minRole: 'RO',
        },
      ],
    },
    {
      title: 'Finance',
      items: [
        {
          path: '/costs',
          label: 'Cost Center',
          description: 'Dashboards and breakdowns',
          icon: <Money24Regular />,
          minRole: 'RO',
        },
      ],
    },
    {
      title: 'Admin',
      items: [
        {
          path: '/integrations',
          label: 'Integrations',
          description: 'Third-party sync',
          icon: <PlugConnected24Regular />,
          minRole: 'Admin',
        },
        {
          path: '/audit',
          label: 'Audit Log',
          description: 'Security & changes',
          icon: <History24Regular />,
          minRole: 'Admin',
        },
        {
          path: '/settings',
          label: 'Settings',
          description: 'Sync, roles, config',
          icon: <Settings24Regular />,
          minRole: 'Admin',
        },
      ],
    },
  ];

  return (
    <div className={styles.page}>
      {/* ── Profile card ─────────────────────────────────────── */}
      <div className={styles.profileCard}>
        <Avatar
          name={currentUser.displayName}
          initials={getInitials(currentUser.displayName)}
          size={56}
          color="brand"
        />
        <div className={styles.profileInfo}>
          <span className={styles.profileName}>{currentUser.displayName}</span>
          <span className={styles.profileEmail}>{currentUser.email}</span>
          <Badge
            appearance="filled"
            color={ROLE_COLORS[currentUser.role] ?? 'informative'}
            size="small"
            className={styles.profileBadge}
          >
            {currentUser.role}
          </Badge>
        </div>
      </div>

      {/* ── Grouped menu sections ────────────────────────────── */}
      {sections.map((section) => {
        const visibleItems = section.items.filter((i) =>
          canAccess(i.minRole, currentUser.role),
        );
        if (visibleItems.length === 0) return null;
        return (
          <div key={section.title}>
            <div className={styles.sectionHeader}>{section.title}</div>
            <div className={styles.section}>
              {visibleItems.map((item) => (
                <div
                  key={item.path}
                  className={styles.row}
                  onClick={() => navigate(item.path)}
                >
                  <div className={styles.rowIcon}>{item.icon}</div>
                  <div className={styles.rowLabel}>
                    <span className={styles.rowTitle}>{item.label}</span>
                    {item.description && (
                      <span className={styles.rowDesc}>{item.description}</span>
                    )}
                  </div>
                  <ChevronRight24Regular className={styles.rowChevron} />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* ── Sign out ─────────────────────────────────────────── */}
      <div>
        <Divider style={{ margin: '0 0 12px' }} />
        <div className={styles.section}>
          <div
            className={`${styles.row} ${styles.dangerRow}`}
            onClick={logout}
          >
            <div className={`${styles.rowIcon} ${styles.dangerIcon}`}>
              <SignOut24Regular />
            </div>
            <div className={styles.rowLabel}>
              <span className={styles.rowTitle} style={{ color: 'inherit' }}>
                Sign out
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
